import { useEffect, useMemo, useState } from 'react';
import {
  AdminRole,
  AdminUserWithRoles,
  CreateAdminRequest,
  UpdateAdminAssignmentsRequest,
} from '../../types/admin-roles';
import CreateAdminDialog from './CreateAdminDialog';
import TypedConfirmDialog from '../TypedConfirmDialog';
import { ToastView, useToast } from '../promotions/useToast';

interface AdminsTabProps {
  admins: AdminUserWithRoles[];
  roles: AdminRole[];
  currentUserId: string | undefined;
  search: string;
  onSearchChange: (value: string) => void;
  onCreate: (body: CreateAdminRequest) => Promise<void>;
  onUpdate: (subjectId: string, body: UpdateAdminAssignmentsRequest) => Promise<void>;
  onDelete: (subjectId: string) => Promise<void>;
  onResetPassword: (subjectId: string) => Promise<void>;
}

export default function AdminsTab({
  admins,
  roles,
  currentUserId,
  search,
  onSearchChange,
  onCreate,
  onUpdate,
  onDelete,
  onResetPassword,
}: AdminsTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingRoleIds, setPendingRoleIds] = useState<string[]>([]);
  const [pendingSuperAdmin, setPendingSuperAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmSelfRevoke, setConfirmSelfRevoke] = useState(false);
  const [resetting, setResetting] = useState<AdminUserWithRoles | null>(null);
  const [deleting, setDeleting] = useState<AdminUserWithRoles | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { toast, show } = useToast();

  const selected = useMemo(
    () => admins.find((a) => a.subjectId === selectedId) ?? null,
    [admins, selectedId],
  );

  // Re-seed the draft whenever the selected admin's server state changes, so a
  // save elsewhere does not leave stale checkboxes behind.
  useEffect(() => {
    if (!selected) return;
    setPendingRoleIds([...selected.roleIds]);
    setPendingSuperAdmin(selected.superAdmin);
  }, [selected]);

  const superAdminCount = useMemo(() => admins.filter((a) => a.superAdmin).length, [admins]);

  const select = (admin: AdminUserWithRoles) => {
    setError(null);
    setSelectedId(admin.subjectId);
  };

  const toggleRole = (id: string) =>
    setPendingRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  const hasChanges = useMemo(() => {
    if (!selected) return false;
    if (selected.superAdmin !== pendingSuperAdmin) return true;
    const before = new Set(selected.roleIds);
    if (before.size !== pendingRoleIds.length) return true;
    return pendingRoleIds.some((id) => !before.has(id));
  }, [selected, pendingRoleIds, pendingSuperAdmin]);

  const wouldLeaveZeroSuperAdmins =
    !!selected && selected.superAdmin && !pendingSuperAdmin && superAdminCount <= 1;

  const isSelfRevoke =
    !!selected && selected.subjectId === currentUserId && selected.superAdmin && !pendingSuperAdmin;

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await onUpdate(selected.subjectId, {
        roleIds: pendingRoleIds,
        superAdmin: pendingSuperAdmin,
      });
      setConfirmSelfRevoke(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update this admin');
    } finally {
      setSaving(false);
    }
  };

  // #954 — the server refuses these too (409); checking first gives a clear message without a round trip.
  const deleteBlockedReason = (admin: AdminUserWithRoles): string | null => {
    if (admin.subjectId === currentUserId) return 'You cannot delete your own account.';
    if (admin.superAdmin && superAdminCount <= 1) return 'The last Super Admin cannot be deleted.';
    return null;
  };

  const askDelete = (admin: AdminUserWithRoles) => {
    const blocked = deleteBlockedReason(admin);
    if (blocked) {
      show(blocked, 'error');
      return;
    }
    setActionError(null);
    setDeleting(admin);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await onDelete(deleting.subjectId);
      if (selectedId === deleting.subjectId) setSelectedId(null);
      show(`${deleting.email} was deleted`);
      setDeleting(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not delete this account');
    } finally {
      setActionBusy(false);
    }
  };

  const confirmReset = async () => {
    if (!resetting) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await onResetPassword(resetting.subjectId);
      show(`Reset link sent to ${resetting.email}`);
      setResetting(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not send the reset link');
    } finally {
      setActionBusy(false);
    }
  };

  const fullName = (a: AdminUserWithRoles) =>
    [a.firstName, a.lastName].filter(Boolean).join(' ') || '—';

  return (
    <>
      <div className="admin-roles-layout">
        <div className="admin-card admin-roles-list">
          <div className="admin-card-header admin-card-header-actions">
            <input
              className="admin-input"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label="Search admins"
            />
            <button type="button" className="admin-btn admin-btn-primary" onClick={() => setCreating(true)}>
              Add admin
            </button>
          </div>

          {admins.length === 0 ? (
            <div className="admin-empty">No admin users found.</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Access</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr
                      key={admin.subjectId}
                      className={admin.subjectId === selectedId ? 'admin-row-selected' : ''}
                      onClick={() => select(admin)}
                    >
                      <td>{fullName(admin)}</td>
                      <td>{admin.email}</td>
                      <td>
                        {admin.superAdmin ? (
                          <span className="admin-badge admin-badge-role">Super Admin</span>
                        ) : admin.roleNames.length === 0 ? (
                          <span className="admin-text-muted">No access</span>
                        ) : (
                          admin.roleNames.map((name) => (
                            <span key={name} className="admin-badge admin-badge-role">{name}</span>
                          ))
                        )}
                      </td>
                      <td>
                        <span className={`admin-badge ${admin.status === 'ACTIVE' ? 'approved' : 'rejected'}`}>
                          {admin.status}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="admin-table-actions">
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-outline"
                            onClick={() => { setActionError(null); setResetting(admin); }}
                          >
                            Reset password
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-danger"
                            onClick={() => askDelete(admin)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="admin-card admin-roles-panel">
          {!selected ? (
            <div className="admin-empty">Select an admin to change their access.</div>
          ) : (
            <>
              <h3 className="admin-card-title">{selected.email}</h3>
              <p className="admin-text-muted">{fullName(selected)}</p>

              <div className="admin-roles-checkbox-list">
                {roles.length === 0 && (
                  <p className="admin-text-muted">No roles exist yet. Create one on the Roles tab.</p>
                )}
                {roles.map((role) => (
                  <label key={role.id} className="admin-roles-checkbox-item">
                    <input
                      type="checkbox"
                      checked={pendingRoleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                      disabled={pendingSuperAdmin}
                    />
                    <div>
                      <div className="admin-roles-checkbox-label">{role.name}</div>
                      <div className="admin-text-muted">
                        {role.description || `${role.screenKeys.length} screen(s)`}
                      </div>
                    </div>
                  </label>
                ))}

                <label className="admin-roles-checkbox-item">
                  <input
                    type="checkbox"
                    checked={pendingSuperAdmin}
                    onChange={() => setPendingSuperAdmin((v) => !v)}
                  />
                  <div>
                    <div className="admin-roles-checkbox-label">Super Admin</div>
                    <div className="admin-text-muted">
                      Every screen, including role management. Overrides the roles above.
                    </div>
                  </div>
                </label>
              </div>

              {wouldLeaveZeroSuperAdmins && (
                <div className="admin-error">At least one Super Admin must exist.</div>
              )}
              {error && <div className="admin-error">{error}</div>}

              <div className="admin-modal-footer admin-roles-actions">
                <button
                  type="button"
                  className="admin-btn"
                  onClick={() => setSelectedId(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={!hasChanges || wouldLeaveZeroSuperAdmins || saving}
                  onClick={() => (isSelfRevoke ? setConfirmSelfRevoke(true) : save())}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </>
          )}
        </aside>
      </div>

      {creating && (
        <CreateAdminDialog roles={roles} onCreate={onCreate} onClose={() => setCreating(false)} />
      )}

      {resetting && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reset-confirm-title">
          <div className="admin-modal admin-modal-sm">
            <div className="admin-modal-header">
              <h3 id="reset-confirm-title">Send a password reset link?</h3>
            </div>
            <div className="admin-modal-body">
              <p>
                {resetting.email} will get an email with a link to set a new password. Their current
                password keeps working until they use the link.
              </p>
              {actionError && <div className="admin-error">{actionError}</div>}
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn" onClick={() => setResetting(null)} disabled={actionBusy}>
                Cancel
              </button>
              <button type="button" className="admin-btn admin-btn-primary" onClick={confirmReset} disabled={actionBusy}>
                {actionBusy ? 'Sending…' : 'Send reset link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <TypedConfirmDialog
          title={`Delete ${deleting.email}?`}
          message={
            actionError
              ?? `${fullName(deleting)} (${deleting.email}) loses staff access and is signed out everywhere. A staff-only account is removed; a login that is also a customer or partner account keeps that access. This cannot be undone.`
          }
          confirmPhrase="DELETE"
          confirmLabel={actionBusy ? 'Deleting…' : 'Delete account'}
          danger
          onConfirm={confirmDelete}
          onCancel={() => { setActionError(null); setDeleting(null); }}
        />
      )}

      <ToastView toast={toast} />

      {confirmSelfRevoke && (
        <TypedConfirmDialog
          title="Remove your own Super Admin role?"
          message="You are removing Super Admin from your own account. You will lose access to this page as soon as the change takes effect."
          confirmPhrase="REMOVE SUPER ADMIN"
          confirmLabel="Remove role"
          danger
          onConfirm={save}
          onCancel={() => setConfirmSelfRevoke(false)}
        />
      )}
    </>
  );
}
